//FleetX-Security-CRUD/web-admin/src/components/AdminShell.jsx
import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import api from "../api/axios";
import { canSeeSidebarItem, normalizeRole, sidebarItems } from "../config/permissions";
import { useAuth } from "../context/AuthContext";
import useBrandingLogo from "../hooks/useBrandingLogo";
import { getNotificationSeverity } from "../utils/monitoringSeverity";
import { getUserDisplayName } from "../utils/userDisplayName";
import "../styles/admin.css";

const navIcons = {
  dashboard: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 13h6V4H4v9Zm0 7h6v-5H4v5Zm10 0h6v-9h-6v9Zm0-11h6V4h-6v5Z" />
    </svg>
  ),
  analytics: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 19h16v2H4v-2Zm1-8h3v6H5v-6Zm5-5h3v11h-3V6Zm5 3h3v8h-3V9Zm4-5h3v13h-3V4Z" />
    </svg>
  ),
  marketing: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M20 4v14.2l-5.4-2.7H11v3.3c0 .7-.5 1.2-1.2 1.2H7.9c-.6 0-1.1-.4-1.2-1L6 15.5H4.5A2.5 2.5 0 0 1 2 13V9a2.5 2.5 0 0 1 2.5-2.5h10.1L20 4Zm-6 4.5H4.5A.5.5 0 0 0 4 9v4c0 .3.2.5.5.5H14v-5Zm2 .3v4.4l2 1V7.8l-2 1Z" />
    </svg>
  ),
  users: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M16 11a4 4 0 1 0-3.6-5.7A4 4 0 0 1 16 11ZM8 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm0 2c-3.3 0-6 1.7-6 3.8V20h12v-3.2C14 14.7 11.3 13 8 13Zm8 0c-.7 0-1.4.1-2 .2 1.2.9 2 2.1 2 3.6V20h6v-3.2c0-2.1-2.7-3.8-6-3.8Z" />
    </svg>
  ),
  vehicles: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5.2 7.5 6.4 4h11.2l1.2 3.5 1.7.5c.9.3 1.5 1.1 1.5 2.1V17h-2v3h-3v-3H7v3H4v-3H2v-6.9c0-1 .6-1.8 1.5-2.1l1.7-.5ZM7.8 6 7 8h10l-.8-2H7.8ZM5 13a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Zm14 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" />
    </svg>
  ),
  bookings: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 2h2v3h6V2h2v3h3v17H4V5h3V2Zm11 9H6v9h12v-9ZM6 7v2h12V7H6Zm2 6h3v3H8v-3Zm5 0h3v3h-3v-3Z" />
    </svg>
  ),
  gps: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2a7 7 0 0 0-7 7c0 5.2 7 13 7 13s7-7.8 7-13a7 7 0 0 0-7-7Zm0 9.7A2.7 2.7 0 1 1 12 6.3a2.7 2.7 0 0 1 0 5.4Z" />
    </svg>
  ),
  drivers: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm0 2c-3.9 0-7 2-7 4.5V21h14v-2.5c0-2.5-3.1-4.5-7-4.5Zm7.5-4.2 1.4-1.4 1.4 1.4-1.4 1.4 1.4 1.4-1.4 1.4-1.4-1.4-1.4 1.4-1.4-1.4 1.4-1.4-1.4-1.4 1.4-1.4 1.4 1.4Z" />
    </svg>
  ),
  settings: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M19.4 13.5c.1-.5.1-1 .1-1.5s0-1-.1-1.5l2-1.5-2-3.4-2.4 1a8 8 0 0 0-2.6-1.5L14 2h-4l-.4 2.6A8 8 0 0 0 7 6.1l-2.4-1-2 3.4 2 1.5c-.1.5-.1 1-.1 1.5s0 1 .1 1.5l-2 1.5 2 3.4 2.4-1a8 8 0 0 0 2.6 1.5L10 22h4l.4-2.6a8 8 0 0 0 2.6-1.5l2.4 1 2-3.4-2-1.5ZM12 15.5a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7Z" />
    </svg>
  ),
  audit: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2 5 5v6c0 4.4 2.8 8.5 7 10 4.2-1.5 7-5.6 7-10V5l-7-3Zm0 2.2 5 2.1V11c0 3.3-2 6.4-5 7.7C9 17.4 7 14.3 7 11V6.3l5-2.1Zm-1 9.9 5.1-5.1 1.4 1.4-6.5 6.5-3.5-3.5 1.4-1.4 2.1 2.1Z" />
    </svg>
  ),
};

function NavIcon({ type }) {
  return <span className="admin-shell-nav__icon">{navIcons[type]}</span>;
}

const EYEBROW_SECTION_MAP = {
  "OVERVIEW": "Overview",
  "BOOKINGS": "Rental Operations",
  "FLEET": "Fleet",
  "FLEET MONITORING": "Operations",
  "ADMINISTRATION": "Administration",
  "BUSINESS INSIGHTS": "Business Insights",
  "CUSTOMER GROWTH": "Growth",
  "SETTINGS": "System",
};

function safeNotificationTarget(value) {
  const target = String(value || "").trim();
  if (!target.startsWith("/") || target.startsWith("//") || target.includes("://")) return null;
  const pathname = target.split(/[?#]/, 1)[0];
  if (pathname.split("/").includes("..")) return null;
  return target;
}

function resolveNotificationDestination(notification) {
  const notificationType = String(notification?.type || "").toLowerCase();
  const explicitTarget = safeNotificationTarget(notification?.targetUrl);
  const isLiveMonitoring = notificationType.startsWith("gps_route_deviation") ||
    notificationType.startsWith("gps_offline");
  const pathname = explicitTarget || (isLiveMonitoring
    ? "/gps-tracking"
    : notificationType.startsWith("verification") ? "/users" : "/bookings");

  return {
    pathname,
    state: pathname.startsWith("/bookings") && notification?.bookingId
      ? { bookingId: notification.bookingId }
      : undefined,
  };
}

export default function AdminShell({
  title,
  subtitle,
  eyebrow = "FLEET",
  actions = null,
  hideTopbarCopy = false,
  pageClassName = "",
  children,
}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const notificationRef = useRef(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationError, setNotificationError] = useState("");
  const [confirmClearAll, setConfirmClearAll] = useState(false);
  const logoUrl = useBrandingLogo();

  const role = normalizeRole(user?.role);

  const roleLabel =
    role === "technical_admin" || role === "technicaladmin"
      ? "Technical Admin"
      : role === "admin" || role === "super_admin"
      ? "Admin"
      : role === "staff"
      ? "Staff"
      : role === "owner"
      ? "Owner"
      : user?.role
      ? String(user.role)
      : "Admin User";

  const userName = getUserDisplayName(user, user?.email || roleLabel);
  const isDashboardShell = pageClassName
    .split(/\s+/)
    .includes("admin-shell--dashboard");
  const usesPremiumAccountCluster = [
    "admin-shell--dashboard",
    "admin-shell--bookings",
    "admin-shell--vehicles",
    "admin-shell--drivers",
    "admin-shell--live-monitoring",
    "admin-shell--rental-management",
    "admin-shell--analytics",
    "admin-shell--marketing",
    "admin-shell--settings",
    "admin-shell--audit-trails",
  ].some((className) => pageClassName.split(/\s+/).includes(className));

  const isTechnicalAdmin = role === "technical_admin" || role === "technicaladmin";

  const navItems = sidebarItems.map((item) => ({
    ...item,
    label:
      item.key === "users" && isTechnicalAdmin
        ? item.technicalAdminLabel || item.label
        : item.label,
    visible: canSeeSidebarItem(role, item.key),
  }));

  const handleLogout = (event) => {
    event?.stopPropagation();
    console.log("[Sidebar] Logout clicked");
    logout();
    navigate("/login");
  };

  const refreshNotifications = useCallback(async () => {
    if (!user?.role) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    try {
      const res = await api.get("/notifications");
      setNotifications(res.data.notifications || []);
      setUnreadCount(Number(res.data.unreadCount || 0));
      setNotificationError("");
    } catch {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [user?.role]);

  useEffect(() => {
    refreshNotifications();
    const timer = window.setInterval(refreshNotifications, 60000);
    window.addEventListener("notifications-refresh", refreshNotifications);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener("notifications-refresh", refreshNotifications);
    };
  }, [refreshNotifications]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!notificationRef.current?.contains(event.target)) {
        setNotificationsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNotificationClick = async (notification) => {
    try {
      if (!notification.isRead) {
        await api.patch(`/notifications/${notification._id}/read`);
        await refreshNotifications();
      }
    } catch {
      // Keep navigation responsive even if marking read fails.
    }

    setNotificationsOpen(false);
    const destination = resolveNotificationDestination(notification);
    navigate(destination.pathname, { state: destination.state });
  };

  const handleMarkAllRead = async () => {
    try {
      await api.patch("/notifications/read-all");
      setNotifications((current) =>
        current.map((item) => ({ ...item, isRead: true, readAt: item.readAt || new Date().toISOString() }))
      );
      setUnreadCount(0);
      setNotificationError("");
    } catch {
      setNotificationError("Unable to mark notifications as read.");
    }
  };

  const handleClearNotification = async (event, notification) => {
    event.stopPropagation();

    try {
      await api.delete(`/notifications/${notification._id}`);
      setNotifications((current) =>
        current.filter((item) => item._id !== notification._id)
      );
      if (!notification.isRead) {
        setUnreadCount((current) => Math.max(0, current - 1));
      }
      setNotificationError("");
    } catch {
      setNotificationError("Unable to clear notification.");
    }
  };

  const handleClearNotifications = async () => {
    try {
      await api.delete("/notifications");
      setNotifications([]);
      setUnreadCount(0);
      setNotificationError("");
      setConfirmClearAll(false);
    } catch {
      setNotificationError("Unable to clear all notifications.");
      setConfirmClearAll(false);
    }
  };

  return (
    <div className={`admin-shell ${pageClassName}`.trim()}>
      <aside className="admin-shell-sidebar">
        <div className="admin-shell-brand">
          <img
            src={logoUrl}
            alt="FleetX Logo"
            className="admin-shell-brand__logo"
          />
          <div className="admin-shell-brand__text">
            <div className="admin-shell-brand__title">
              FleetX
              <br />
              Transport Services
            </div>
          </div>
        </div>

        <nav className="admin-shell-nav" aria-label="Admin navigation">
          {[
            {
              groupLabel: "Overview",
              keys: ["dashboard"],
            },
            {
              groupLabel: "Rental Operations",
              keys: ["bookings", "vehicles", "drivers", "gpsTracking"],
            },
            {
              groupLabel: "Administration",
              keys: ["users"],
            },
            {
              groupLabel: "Insights & Growth",
              keys: ["analyticsReports", "marketingCampaigns"],
            },
            {
              groupLabel: "System",
              keys: ["settings", "auditTrails"],
            },
          ].map(({ groupLabel, keys }) => {
            const groupItems = keys
              .map((key) => navItems.find((item) => item.key === key))
              .filter((item) => item && item.visible);

            if (groupItems.length === 0) return null;

            return (
              <div key={groupLabel} className="admin-shell-nav__group">
                <span className="admin-shell-nav__group-label">{groupLabel}</span>
                {groupItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      isActive
                        ? "admin-shell-nav__link active"
                        : "admin-shell-nav__link"
                    }
                    onClick={() => {
                      if (item.key === "auditTrails") {
                        console.log("[Sidebar] Audit Trails clicked");
                      }
                    }}
                  >
                    <NavIcon type={item.icon} />
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        <div className="admin-shell-sidebar__bottom">
          <button
            type="button"
            className="admin-shell-logout"
            onClick={handleLogout}
          >
            Sign out
          </button>
        </div>
      </aside>

      <main className="admin-shell-main">
        <div className="admin-shell-page">
          <div className="admin-shell-topbar">
            <div className="admin-shell-topbar__context">
              <span className="admin-shell-topbar__title">{title}</span>
              {EYEBROW_SECTION_MAP[eyebrow] ? (
                <span className="admin-shell-topbar__section">
                  {EYEBROW_SECTION_MAP[eyebrow]}
                </span>
              ) : null}
            </div>
            <div className="admin-shell-topbar__right">
              <div className="admin-notifications" ref={notificationRef}>
                <button
                  type="button"
                  className="admin-notifications__button"
                  onClick={() => setNotificationsOpen((prev) => !prev)}
                  aria-label="Notifications"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 22a2.5 2.5 0 0 0 2.3-1.5H9.7A2.5 2.5 0 0 0 12 22Zm7-6-1.8-2.2V10a5.2 5.2 0 0 0-4-5.1V3a1.2 1.2 0 0 0-2.4 0v1.9a5.2 5.2 0 0 0-4 5.1v3.8L5 16v2h14v-2Z" />
                  </svg>
                  {unreadCount > 0 ? <em>{unreadCount > 9 ? "9+" : unreadCount}</em> : null}
                </button>

                {notificationsOpen ? (
                  <div className="admin-notifications__dropdown">
                    <div className="admin-notifications__header">
                      <strong>Notifications</strong>
                      <div className="admin-notifications__actions">
                        <button type="button" onClick={handleMarkAllRead}>
                          Mark read
                        </button>
                        <button type="button" onClick={() => setConfirmClearAll(true)}>
                          Clear All
                        </button>
                      </div>
                    </div>
                    {notificationError ? (
                      <div className="admin-notifications__error">{notificationError}</div>
                    ) : null}
                    <div className="admin-notifications__list">
                      {notifications.length ? (
                        notifications.map((item) => (
                          <div
                            key={item._id}
                            className={`admin-notifications__item ${
                              item.isRead ? "" : "is-unread"
                            }`}
                            data-severity={getNotificationSeverity(item) || undefined}
                            onClick={() => handleNotificationClick(item)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                handleNotificationClick(item);
                              }
                            }}
                          >
                            <button
                              type="button"
                              className="admin-notifications__clear"
                              aria-label="Clear notification"
                              onClick={(event) => handleClearNotification(event, item)}
                            >
                              &times;
                            </button>
                            <strong>{item.title}</strong>
                            <span>{item.message}</span>
                            <small>{new Date(item.createdAt).toLocaleString()}</small>
                          </div>
                        ))
                      ) : (
                        <div className="admin-notifications__empty">
                          No notifications yet.
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
              <span className="admin-shell-topbar__chip">{roleLabel}</span>
              <div className="admin-shell-topbar__user">
                <span className="admin-shell-topbar__avatar" aria-hidden="true">
                  {(() => {
                    const words = (userName || roleLabel || "A")
                      .trim()
                      .split(/\s+/)
                      .filter(Boolean);
                    if (words.length >= 2) {
                      return (words[0][0] + words[words.length - 1][0]).toUpperCase();
                    }
                    return (words[0] || "A")[0].toUpperCase();
                  })()}
                </span>
                <span className="admin-shell-topbar__account-copy">
                  <span className="admin-shell-topbar__username">
                    {usesPremiumAccountCluster ? roleLabel : userName}
                  </span>
                  {usesPremiumAccountCluster ? (
                    <span className="admin-shell-topbar__account-subtitle">
                      FleetX Technical Services
                    </span>
                  ) : null}
                </span>
                {isDashboardShell ? (
                  <span className="admin-shell-topbar__chevron" aria-hidden="true">
                    <svg viewBox="0 0 20 20">
                      <path d="m6.5 8 3.5 3.5L13.5 8" />
                    </svg>
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          <header className="admin-shell-page__head">
            <div className="admin-shell-page__titleBlock">
              <div className="admin-shell-page__eyebrow">{eyebrow}</div>
              <h1 className="admin-shell-page__title">{title}</h1>
              {subtitle ? (
                <p className="admin-shell-page__subtitle">{subtitle}</p>
              ) : null}
            </div>

            {actions ? (
              <div className="admin-shell-page__actions">{actions}</div>
            ) : null}
          </header>

          <section className="admin-shell-page__content">{children}</section>
        </div>
      </main>
      {confirmClearAll ? (
        <div
          className="admin-notifications__modalOverlay"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={() => setConfirmClearAll(false)}
        >
          <div
            className="admin-notifications__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-clear-notifications-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="admin-clear-notifications-title">Clear all notifications?</h3>
            <p>
              This will remove all notifications from your account only. Related
              booking, payment, and verification records will not be deleted.
            </p>
            <div className="admin-notifications__modalActions">
              <button type="button" onClick={() => setConfirmClearAll(false)}>
                Cancel
              </button>
              <button type="button" onClick={handleClearNotifications}>
                Clear All
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
